import * as React from "react";

export type GummyElevation = "max" | "flat";

/** @deprecated Gummy is the only style. Retained for callers that branch on it. */
export type GummyThemeMode = "legacy" | "gummy";

const readElevation = (): GummyElevation => {
  if (typeof document === "undefined") return "max";
  return (document.body.dataset.themeElevation as GummyElevation) ?? "max";
};

/**
 * Tracks `body[data-theme-elevation]`. The host app sets this via its
 * settings layer — this hook just reads and re-renders on change so any
 * consumer (e.g. a motion `whileHover` lift) can branch on it.
 */
export const useElevation = (): GummyElevation => {
  const subscribe = React.useCallback((onStoreChange: () => void) => {
    if (typeof document === "undefined") return () => {};
    const observer = new MutationObserver(onStoreChange);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-theme-elevation"]
    });
    return () => observer.disconnect();
  }, []);
  return React.useSyncExternalStore(
    subscribe,
    readElevation,
    () => "max" as GummyElevation
  );
};

export const useIsFlat = (): boolean => useElevation() === "flat";

/** @deprecated Gummy is now the only style — always returns "gummy". */
export const useGummyTheme = (): GummyThemeMode => "gummy";

/** @deprecated Gummy is now the only style — always returns true. */
export const useIsGummy = (): boolean => true;

/**
 * @deprecated Elevation is now driven from the host app's settings layer
 * (which writes `body[data-theme-elevation]`). This provider is a no-op kept
 * for back-compat with any old call sites.
 */
export type GummyThemeProviderProps = {
  elevation?: GummyElevation;
  children: React.ReactNode;
};
export const GummyThemeProvider = ({
  elevation,
  children
}: GummyThemeProviderProps) => {
  React.useEffect(() => {
    if (elevation) document.body.dataset.themeElevation = elevation;
  }, [elevation]);
  return <>{children}</>;
};
